//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';

import {
  getShopTags,
  searchInstagramShopTagCatalog,
} from '@apis/instagram-shops/instagramShopsApi';
import {
  IInstagramShopsCatalog,
  IInstagramShopsProduct,
} from '@apis/instagram-shops/instagramShopsApiModel';
import { SimpleCache } from '@hooks/useSimpleCache';
import { ProductSelectItemInfo } from '@instagram-account/instagram-shops/InstagramShopsUIConstants';
import { logFailedShopTags } from '@instagram-account/post-details/postDetailsUIUtils';
import {
  requestFeatureOnInstagramShopsFailedToast,
  requestInstagramShopsCatalogsFailedToast,
} from '@instagram-account/redux/instagram-shops/instagramShopsToasts';
import { IInstagramShopsProductSearchModalState } from '@instagram-account/redux/instagram-shops/product-search-modal/instagramShopsProductSearchModalModel';
import { getInstagramShopsProductSearchModalState } from '@instagram-account/redux/instagram-shops/selectors/getInstagramShops';
import {
  getNewCatalogSelection,
  getSelectedCatalog,
  getSelectedCatalogId,
} from '@instagram-account/redux/instagram-shops/selectors/getSelectedCatalog';
import {
  getUpdatedProductSelections,
  getCurrentSelectedProducts,
  getSelectedProductMatch,
} from '@instagram-account/redux/instagram-shops/selectors/getUpdatedProductSelections';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import Cuid from '@models/Cuid';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: IInstagramShopsProductSearchModalState = {
  selectedCatalog: undefined,
  searchResults: [],
  productMatches: [],
  productVariantsMap: PojoMap.empty<string, IInstagramShopsProduct[]>(),
  selectedProducts: [],
  selectedVariants: PojoMap.empty<string, IInstagramShopsProduct>(),
  currentCuid: undefined,
  flags: {
    isFetchingSearchResults: false,
    isFetchingProductMatches: false,
    isProductSearchModalOpen: false,
  },
  errors: {},
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramShopsProductSearchModal,
  initialState,
  reducers: {
    requestInstagramShopsSearchProducts(state) {
      state.flags.isFetchingSearchResults = true;
      state.errors.fetchSearchResultsError = undefined;
    },
    receiveInstagramShopsSearchProductsCancelled(state) {
      state.flags.isFetchingSearchResults = false;
    },
    receiveInstagramShopsSearchProductsSucceeded(
      state,
      action: PayloadAction<IInstagramShopsProduct[]>,
    ) {
      state.flags.isFetchingSearchResults = false;
      state.searchResults = action.payload;
    },
    receiveInstagramShopsSearchProductsFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.flags.isFetchingSearchResults = false;
      state.errors.fetchSearchResultsError = action.payload;
    },
    requestInstagramShopsProductMatches(state) {
      state.flags.isFetchingProductMatches = true;
      state.errors.fetchSearchResultsError = undefined;
    },
    receiveInstagramShopsProductMatchesSucceeded(
      state,
      action: PayloadAction<IInstagramShopsProduct[]>,
    ) {
      state.flags.isFetchingProductMatches = false;
      state.productMatches = action.payload;
    },
    receiveInstagramShopsProductMatchesFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.flags.isFetchingProductMatches = false;
      state.errors.fetchProductMatchesError = action.payload;
    },
    setIsModalOpen(state, action: PayloadAction<boolean>) {
      state.flags.isProductSearchModalOpen = action.payload;
    },
    setCurrentCuid(state, action: PayloadAction<Cuid | undefined>) {
      state.currentCuid = action.payload;
    },
    setSelectedCatalog(
      state,
      action: PayloadAction<IInstagramShopsCatalog | undefined>,
    ) {
      state.selectedCatalog = action.payload;
    },
    setSelectedProducts(
      state,
      action: PayloadAction<{
        selectedProducts: IInstagramShopsProduct[];
        productVariantsMap: PojoMap<string, IInstagramShopsProduct[]>;
      }>,
    ) {
      state.selectedProducts = action.payload.selectedProducts;
      state.productVariantsMap = action.payload.productVariantsMap;
    },
    setSelectedVariants(
      state,
      action: PayloadAction<PojoMap<string, IInstagramShopsProduct>>,
    ) {
      state.selectedVariants = action.payload;
    },
  },
});

export const searchInstagramShopsProducts = createThunk(
  (
      searchQuery: string,
      abortController: AbortController,
      cache: SimpleCache<IInstagramShopsProduct[]>,
    ) =>
    async (dispatch, getState) => {
      const selectedCatalog = getSelectedCatalog(getState());

      if (!selectedCatalog) {
        dispatch(
          toastNotificationsActions.addToastNotification(
            requestInstagramShopsCatalogsFailedToast,
          ),
        );
        return;
      }

      dispatch(actions.requestInstagramShopsSearchProducts());

      const cacheResult = cache.get(searchQuery);

      if (isDefined(cacheResult)) {
        dispatch(
          actions.receiveInstagramShopsSearchProductsSucceeded(cacheResult),
        );
        return;
      }

      try {
        const products = await searchInstagramShopTagCatalog(
          {
            catalogId: selectedCatalog.catalogId,
            searchQuery,
          },
          abortController.signal,
        );

        cache.set(searchQuery, products);

        dispatch(
          actions.receiveInstagramShopsSearchProductsSucceeded(products),
        );
      } catch (e) {
        if (abortController.signal.aborted) {
          dispatch(actions.receiveInstagramShopsSearchProductsCancelled());
        } else {
          dispatch(
            actions.receiveInstagramShopsSearchProductsFailed(
              getRequestErrorMessage(e) ??
                `Failed to query for product matches in search ${searchQuery}`,
            ),
          );
          dispatch(
            toastNotificationsActions.addToastNotification(
              requestFeatureOnInstagramShopsFailedToast,
            ),
          );
        }
      }
    },
);

export const fetchMatchingInstagramProducts = createThunk(
  (products: ProductSelectItemInfo[]) => async (dispatch, getState) => {
    const selectedCatalog = getSelectedCatalog(getState());

    if (!selectedCatalog) {
      return;
    }

    dispatch(actions.requestInstagramShopsProductMatches());

    try {
      const selectedCatalogId = selectedCatalog.catalogId;
      const productNames = products.map((product) => product.name);

      // Search the instagram api for matching products by name
      const instagramProducts = (
        await Promise.all(
          productNames.map((name) =>
            searchInstagramShopTagCatalog({
              catalogId: selectedCatalog.catalogId,
              searchQuery: name,
            }),
          ),
        )
      ).flat();

      // Materialize all of the variant products to show the user
      const instagramProductIdsWithVariants = instagramProducts.flatMap(
        (product) =>
          product.productVariants.length > 0
            ? product.productVariants.map((variant) => variant.productId)
            : [product.productId],
      );

      const instagramProductsWithVariants: IInstagramShopsProduct[] = [];

      try {
        const { successfulShopTags, failedShopTags } = await getShopTags(
          selectedCatalogId,
          {
            shopTagIds: instagramProductIdsWithVariants,
          },
        );

        instagramProductsWithVariants.push(...successfulShopTags);

        logFailedShopTags(failedShopTags);
      } catch (e) {
        console.log(
          `[fetchMatchingInstagramProducts] Error while getting shop tags: ${
            getRequestErrorMessage(e) ?? ''
          }`,
        );
      }

      dispatch(
        actions.receiveInstagramShopsProductMatchesSucceeded(
          instagramProductsWithVariants,
        ),
      );
    } catch (e) {
      dispatch(
        actions.receiveInstagramShopsProductMatchesFailed(
          getRequestErrorMessage(e) ?? 'Failed to query for product matches',
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification(
          requestFeatureOnInstagramShopsFailedToast,
        ),
      );
    }
  },
);

export const selectProductFromSearch = createThunk(
  (selectedProductId: string) => async (dispatch, getState) => {
    const newProductSelections = getUpdatedProductSelections(
      getState(),
      selectedProductId,
    );

    const product = getSelectedProductMatch(getState(), selectedProductId);
    const selectedCatalogId = getSelectedCatalogId(getState());

    const { productVariantsMap } = getInstagramShopsProductSearchModalState(
      getState(),
    );

    const productVariantIds =
      product?.productVariants.map((p) => p.productId) ?? [];

    const productVariants: IInstagramShopsProduct[] = [];

    try {
      const { successfulShopTags, failedShopTags } = await getShopTags(
        selectedCatalogId ?? '',
        {
          shopTagIds: productVariantIds,
        },
      );

      productVariants.push(...successfulShopTags);

      logFailedShopTags(failedShopTags);
    } catch (e) {
      console.log(
        `[selectProductFromSearch] Error while getting shop tags: ${
          getRequestErrorMessage(e) ?? ''
        }`,
      );
    }

    dispatch(
      actions.setSelectedProducts({
        selectedProducts: newProductSelections,
        productVariantsMap: PojoMap.set(
          productVariantsMap,
          selectedProductId,
          productVariants,
        ),
      }),
    );
  },
);

export const selectProductMatch = createThunk(
  (selectedProductId: string) => async (dispatch, getState) => {
    const newProductSelection = getSelectedProductMatch(
      getState(),
      selectedProductId,
    );

    const { productVariantsMap } = getInstagramShopsProductSearchModalState(
      getState(),
    );

    dispatch(
      actions.setSelectedProducts({
        selectedProducts: newProductSelection ? [newProductSelection] : [],
        productVariantsMap,
      }),
    );
  },
);

export const removeProductMatch = createThunk(
  (removedProductId: string) => (dispatch, getState) => {
    const newProductSelections = getCurrentSelectedProducts(getState()).filter(
      (product) => product.productId !== removedProductId,
    );
    const { productVariantsMap } = getInstagramShopsProductSearchModalState(
      getState(),
    );

    dispatch(
      actions.setSelectedProducts({
        selectedProducts: newProductSelections,
        productVariantsMap: PojoMap.remove(
          productVariantsMap,
          removedProductId,
        ),
      }),
    );
    dispatch(
      actions.setSelectedVariants(
        PojoMap.empty<string, IInstagramShopsProduct>(),
      ),
    );
  },
);

export const closeProductSearchModal = createThunk(() => (dispatch) => {
  dispatch(actions.setIsModalOpen(false));
  dispatch(actions.setCurrentCuid(undefined));
  dispatch(
    actions.setSelectedProducts({
      selectedProducts: [],
      productVariantsMap: PojoMap.empty<string, IInstagramShopsProduct[]>(),
    }),
  );
  dispatch(
    actions.setSelectedVariants(
      PojoMap.empty<string, IInstagramShopsProduct>(),
    ),
  );
});

export const openProductSearchModal = createThunk(
  (cuid: Cuid) => (dispatch) => {
    dispatch(actions.setCurrentCuid(cuid));
    dispatch(actions.setIsModalOpen(true));
  },
);

export const updateSelectedCatalog = createThunk(
  (catalogId: string) => (dispatch, getState) => {
    const newCatalog = getNewCatalogSelection(getState(), catalogId);
    dispatch(actions.setSelectedCatalog(newCatalog));
  },
);

export const updateSelectedVariant = createThunk(
  (productId: string, variant: IInstagramShopsProduct) =>
    (dispatch, getState) => {
      const { selectedVariants } =
        getState().instagram.shops.productSearchModal;

      dispatch(
        actions.setSelectedVariants(
          PojoMap.set(selectedVariants, productId, variant),
        ),
      );
    },
);

export default reducer;
